import React, { useState, useRef, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { EventClickArg } from "@fullcalendar/core";
import { Tooltip } from "@mui/material";
import {
  Box,
  Popover,
  Typography,
  Button,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import "./CalendarView.scss"; // Use this file for any additional styling if needed
import { getUser } from "../../utils/types";
import HandleInterviewLink from "../../shared/CommonFunctions/CommonFunction";
import moment from "moment";

interface CalendarViewProps {
  data?: any;
  handleView?: (data?: any) => void;
  handleReschedule?: (data?: any) => void;
  pageName?: string;
  combinedButton?: boolean;
  handleCombinedButton?: (data?: any) => void;
  displayViewActionOnly?: boolean;
  rescheduleText?: string;
  combinedButtonText?: string;
}

const CalendarView: React.FC<CalendarViewProps> = ({
  data,
  handleView = () => {},
  handleReschedule = () => {},
  pageName = "",
  combinedButton = false,
  handleCombinedButton = () => {},
  displayViewActionOnly = false,
  rescheduleText = "",
  combinedButtonText = "",
}) => {
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [view, setView] = useState<string>("timeGridWeek");
  const calendarRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentMonthYear, setCurrentMonthYear] = useState<string>("");
  const [calenderData, setCalenderData] = useState([]);
  const user = getUser();

  const handleEventClick = (clickInfo: EventClickArg) => {
    setSelectedEvent(clickInfo.event);
    setAnchorEl(clickInfo.jsEvent.currentTarget);
  };

  const updateMonthYear = () => {
    const calendarApi = calendarRef.current.getApi();
    const currentDate = calendarApi.getDate();
    const monthYear = currentDate.toLocaleString("en-US", {
      month: "long",
      year: "numeric",
    });
    setCurrentMonthYear(monthYear);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setSelectedEvent(null);
  };

  const handleViewChange = (
    _: React.MouseEvent<HTMLElement>,
    newView: string | null
  ) => {
    if (newView) {
      setView(newView);
      calendarRef.current.getApi().changeView(newView);
      updateMonthYear();
    }
  };

  useEffect(() => {
    if (data) {
      setCalenderData(data);
    }
  }, [data]);

  useEffect(() => {
    updateMonthYear();
  }, []);

  useEffect(() => {
    if (!containerRef.current || !calendarRef.current) return;
    const observer = new ResizeObserver(() => {
      if (calendarRef.current) {
        calendarRef.current.getApi().updateSize();
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const eventContent = (arg: any) => {
    const { title, extendedProps } = arg.event;
    const startTime = arg.event.start;
    const endTime = arg.event.end;
    // Format the time range (e.g., 5:00pm to 6:00pm)
    const formatTime = (date: Date | null) =>
      date
        ? new Intl.DateTimeFormat("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          }).format(date)
        : "";

    const timeRange =
      startTime && endTime
        ? `${formatTime(startTime)} - ${formatTime(endTime)}`
        : arg.timeText;

    // Check if the view is month view
    const isMonthView = arg.view.type === "dayGridMonth";

      function isTimeWithinFourHour(passedDate: any, passedTime: any) {
        const passedDateTime = passedDate + " " + passedTime;
        const passedMoment = moment(passedDateTime, "YYYY-MM-DD HH:mm:ss");
        const currentMoment = moment();
        const differenceInMinutes = passedMoment.diff(currentMoment, "minutes");
        if (passedMoment.isBefore(currentMoment)) {
          return true;
        }
        return differenceInMinutes <= 240;
      }

    return (
      <Box sx={{ width: "100%", height: "100%" }}>
        <Tooltip
          title={
            <Box
              sx={{
                width: "193px",
                maxHeight: "184px",
                bgcolor: "#fff",
                padding: "10px",
                borderRadius: "4px",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: "400", color: "#606060" }}
              >
                Position Title:
                <Box
                  sx={{
                    fontWeight: "600",
                    fontSize: "13px",
                    lineHeight: "15.83px",
                    color: "#000000",
                    whiteSpace:'wrap'
                  }}
                >
                  {title || "NA"}
                </Box>
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#606060", marginTop: "12px", fontWeight: "400" }}
              >
                Interview Link:
              </Typography>
              <Box
                sx={{
                  fontWeight: "600",
                  fontSize: "13px",
                  lineHeight: "15.83px",
                  color: "#000000",
                }}
              >
                <HandleInterviewLink link={extendedProps?.webex_link}/>
              </Box>
              <>
                {" "}
                {pageName !== "dashboard" && combinedButton ? (
                  <Box sx={{ marginTop: "21px", textAlign: "left" }}>
                    <Tooltip title="View" arrow>
                      <Button
                        variant="text"
                        disableRipple
                        sx={{
                          fontSize: "14px",
                          color: "#4880FF",
                          padding: "0px",
                          minWidth: "auto",
                        }}
                        onClick={() => {
                          handleCombinedButton(extendedProps);
                        }}
                      >
                        {combinedButtonText
                          ? combinedButtonText
                          : "View-Reschedule"}
                      </Button>
                    </Tooltip>
                  </Box>
                ) : (
                  pageName !== "dashboard" && (
                    <Box sx={{ marginTop: "21px", textAlign: "left" }}>
                      <Tooltip title="View" arrow>
                        <Button
                          variant="text"
                          disableRipple
                          sx={{
                            fontSize: "14px",
                            color: "#4880FF",
                            padding: "0px",
                            minWidth: "auto",
                          }}
                          onClick={() => {
                            handleView(extendedProps);
                          }}
                        >
                          View
                        </Button>
                      </Tooltip>
                      {user?.roles?.role_code !== "candidate" &&
                        !displayViewActionOnly && (
                          <>
                            <Typography
                              sx={{
                                display: "inline",
                                color: "#4880FF",
                                fontSize: "14px",
                                padding: "0 5px",
                              }}
                            >
                              /
                            </Typography>
                            <Tooltip
                              title={
                                rescheduleText ? rescheduleText : "Reschedule"
                              }
                              arrow
                            >
                              <Button
                                variant="text"
                                sx={{
                                  fontSize: "14px",
                                  color: "#4880FF",
                                  padding: "0",
                                  minWidth: "auto",
                                }}
                                onClick={() => {
                                  handleReschedule(extendedProps);
                                }}
                                disabled={rescheduleText=='Withdrawal'?isTimeWithinFourHour(extendedProps?.slot_date,extendedProps?.start_time):rescheduleText=='View-Assessment'?!(extendedProps?.application_status ==="completed"): false}
                              >
                                {rescheduleText ? rescheduleText : "Reschedule"}
                              </Button>
                            </Tooltip>
                          </>
                        )}
                    </Box>
                  )
                )}
              </>
            </Box>
          }
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "#fff",
                color: "#000",
                borderRadius: "4px",
                boxShadow: 3,
              },
            },
            arrow: {
              sx: {
                color: "#fff",
              },
            },
          }}
          PopperProps={{
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, -20],
                },
              },
            ],
          }}
        >
          <Box
            sx={{
              backgroundColor: "#E7F3FF",
              padding: "16px 6px",
              borderLeft: "4px solid #0ea5e9",
              borderTopLeftRadius: "4px",
              borderBottomLeftRadius: "4px",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#034EA2", fontWeight: "bold", marginBottom: "2px" ,whiteSpace:"pre-wrap"}}
            >
              {isMonthView ? timeRange : arg.timeText}
            </Typography>
            <Typography variant="body2" sx={{ color: "#18181B" ,whiteSpace:'wrap'}}>
              {title}
            </Typography>
          </Box>
        </Tooltip>
      </Box>
    );
  };

  return (
    <Box ref={containerRef}>
      <Box
        sx={{
          display:"flex",
          justifyContent:{xs: "center", md: "space-between"},
          alignItems:"center",
          padding: {xs: 0, md: "8px 16px"},
          marginBottom:"16px",
          flexWrap: {xs: "wrap", md: "unset"},
        }}
      >
        <Box sx={{ display: "flex", gap: "2px" }}>
          <Button
            sx={{
              backgroundColor: "#F4F4F5",
              height: "28px",
              minWidth: "28px",
              color: "#18181B",
              borderRadius: "6px 0 0 6px !important",
            }}
            onClick={() => {
              calendarRef.current.getApi().prev();
              updateMonthYear();
            }}
          >
            &lt;
          </Button>
          <Button
            sx={{
              backgroundColor: "#F4F4F5",
              height: "28px",
              borderRadius: "0 !important",
              color: "#18181B",
            }}
            onClick={() => {
              calendarRef.current.getApi().today();
              updateMonthYear();
            }}
          >
            Today
          </Button>
          <Button
            sx={{
              backgroundColor: "#F4F4F5",
              height: "28px",
              minWidth: "28px",
              color: "#18181B",
              borderRadius: "0 6px 6px 0 !important",
            }}
            onClick={() => {
              calendarRef.current.getApi().next();
              updateMonthYear();
            }}
          >
            &gt;
          </Button>
        </Box>

        <Box display="flex" justifyContent="center" flexGrow={1} sx={{width: {xs: "100%", sm: "auto"}}}>
          <Box
            sx={{
              border: "0.5px solid #CBC8C8",
              borderRadius: "30px",
              padding: "8px",
              margin: {xs: "10px 0", sm: 0}
            }}
          >
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={handleViewChange}
              aria-label="Calendar View Selector"
              sx={{
                "& .MuiToggleButton-root": { border: "none", height: "28px" },
                "& .Mui-selected": {
                  backgroundColor: "#034EA2 !important",
                  color: "#fff !important",
                  height: "28px",
                  padding: "4px 16px",
                  borderRadius: "8px",
                },
                "& .MuiToggleButton-root:not(:last-of-type)": {
                  marginRight: "8px",
                },
              }}
            >
              <ToggleButton
                sx={{ borderRadius: "20px !important" }}
                value="timeGridDay"
              >
                Day
              </ToggleButton>
              <ToggleButton
                sx={{ borderRadius: "20px !important" }}
                value="timeGridWeek"
              >
                Week
              </ToggleButton>
              <ToggleButton
                sx={{ borderRadius: "20px !important" }}
                value="dayGridMonth"
              >
                Month
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
        <Typography variant="h6" className="month-year" sx={{fontSize:{xs: "0.875rem", md: "0.875rem", lg: "1.25rem"}, padding: 0}}>
          {currentMonthYear}
        </Typography>
      </Box>

      {/* FullCalendar Component */}
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView={view}
        headerToolbar={false}
        events={calenderData}
        allDaySlot={false}
        eventContent={eventContent}
        eventClick={handleEventClick}
        fixedWeekCount={false}
        slotEventOverlap={false}
        // showNonCurrentDates={false}
        eventTimeFormat={{
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
          // omitZeroMinute: true
        }}
        slotLabelFormat={{
          hour: "numeric",
          hour12: true,
          omitZeroMinute: true,
        }}
        dayHeaderContent={(args) => {
          if (args.view.type === "dayGridMonth") {
            return (
              <Box
                style={{
                  fontWeight: "700",
                  fontSize: "13px",
                  fontFamily: "Poppins",
                  color: "#71717A",
                }}
              >
                {args.date
                  .toLocaleString("en-US", {
                    weekday: "short",
                  })
                  .toLocaleUpperCase()}
              </Box>
            );
          }
          return (
            <Box sx={{ textAlign: "left", padding: "10px 0" }}>
              <Box
                style={{
                  fontWeight: "700",
                  fontSize: "10px",
                  fontFamily: "Poppins",
                  color: "#71717A",
                }}
              >
                {args.date
                  .toLocaleString("en-US", { weekday: "short" })
                  .toLocaleUpperCase()}
              </Box>
              <Box
                style={{
                  fontWeight: "500",
                  fontSize: "22px",
                  fontFamily: "Poppins",
                  color: "#000",
                }}
              >
                {args.date.getDate()}
              </Box>
            </Box>
          );
        }}
        slotMinTime="07:00:00"
        slotMaxTime="19:00:00"
        slotDuration="01:00:00"
        height={view === "dayGridMonth" ? "auto" : "75vh"}
        contentHeight={view === "dayGridMonth" ? "auto" : "inherit"}
      />
    </Box>
  );
};

export default CalendarView;
